import { View } from "@-/com.npmjs.react-native";
import { useAnimatedRef, useSharedValue } from "@-/com.npmjs.react-native-reanimated";
import { useState, type ReactNode } from "react";
import type { PrimaryNavigationDestination } from "@-/dev.improve.haniverse.database.schemas.actor.schema._tables.user";
import {
  createPrimaryNavigationCustomizationController,
  PrimaryNavigationCustomizationContext,
} from "#primary-navigation-customization-controller";
import type { PrimaryNavigationCustomizationDrag } from "#primary-navigation-customization-drag";
import { PrimaryNavigationCustomizationGhostLayer } from "#primary-navigation-customization-ghost-layer";
import type { PrimaryNavigationStripEditorMetrics } from "#primary-navigation-strip-editor-geometry";

/**
 * Provides the strip customization controller to everything inside, and draws the drag ghost
 * above it. Mount it around both the editable strip and the page that edits it: the native app
 * shell wraps its whole tab layout, and the Settings page wraps itself where no shell exists.
 */
export function PrimaryNavigationCustomizationProvider({ children }: { children: ReactNode }) {
  const available = useSharedValue<readonly PrimaryNavigationDestination[]>([]);
  const drag = useSharedValue<PrimaryNavigationCustomizationDrag | null>(null);
  const ghostLayerRef = useAnimatedRef<View>();
  const ghostOpacity = useSharedValue(0);
  const ghostScale = useSharedValue(1);
  const ghostX = useSharedValue(0);
  const ghostY = useSharedValue(0);
  const lastDragId = useSharedValue(0);
  const order = useSharedValue<readonly PrimaryNavigationDestination[]>([]);
  const stripMetrics = useSharedValue<PrimaryNavigationStripEditorMetrics | null>(null);
  const stripRef = useAnimatedRef<View>();
  const [controller] = useState(() =>
    createPrimaryNavigationCustomizationController({
      available,
      drag,
      ghostLayerRef,
      ghostOpacity,
      ghostScale,
      ghostX,
      ghostY,
      lastDragId,
      order,
      stripMetrics,
      stripRef,
    }),
  );

  return (
    <PrimaryNavigationCustomizationContext value={controller}>
      <View className="flex-1">
        {children}
        <PrimaryNavigationCustomizationGhostLayer controller={controller} />
      </View>
    </PrimaryNavigationCustomizationContext>
  );
}
